import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Chip,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Modal,
  Button,
  Fade,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
  Switch,
} from '@mui/material';
import {
  Search,
  Visibility,
  Edit,
  Save,
  Close,
  Add,
} from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  getSupplierConfigurationsAPI,
  viewSupplierConfigurationAPI,
  updateSupplierConfigurationAPI,
  saveSupplierConfigurationAPI,
} from '../operations/supplierService';

/* ================= TYPES ================= */
interface SupplierData {
  id: number;
  supplier_name: string;
  supplier_code: number;
  supplier_source: string;
  office_id: string | null;
  environment: string;
  timeout: number;
  result_priority: number;
  enable_family_fare: boolean;
  enable_pnr_action: boolean;
  is_active: boolean;
  created_date: string | null;
  modified_date: string;
  supplier_charges: number;
  charges_type: string | null;
  per_booking_charges: number;
  per_booking_charges_type: string | null;
  discount: string;
  discount_type: string | null;
  discount_on: string | null;
  charges_on: string | null;
  encrypted_key?: string;
  credentials?: string;
}

const SupplierConfiguration = () => {
  const [openRowId, setOpenRowId] = useState<number | null>(null);
  const [editRowId, setEditRowId] = useState<number | null>(null);
  // Track how the current open row was opened so Cancel can decide whether
  // to collapse the panel (edit-only) or keep it open (view-first).
  const [openedByEdit, setOpenedByEdit] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [suppliers, setSuppliers] = useState<SupplierData[]>([]);
  const [editedData, setEditedData] = useState<Partial<SupplierData>>({});
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [viewLoading, setViewLoading] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [viewDataMap, setViewDataMap] = useState<Record<number, Partial<SupplierData>>>({});
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMsg, setSnackbarMsg] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error" | "info" | "warning">("success");
  const [newSupplierErrors, setNewSupplierErrors] = useState<Record<string, string>>({});
  const [newSupplier, setNewSupplier] = useState<Partial<SupplierData>>({
    supplier_name: '',
    supplier_code: 0,
    supplier_source: '',
    environment: '',
    office_id: null,
    timeout: 0,
    result_priority: 0,
    enable_family_fare: false,
    enable_pnr_action: false,
    is_active: false,
    supplier_charges: 0,
    charges_type: null,
    per_booking_charges: 0,
    per_booking_charges_type: null,
    discount: '',
    discount_type: null,
    discount_on: null,
    charges_on: null,
    encrypted_key: '',
    credentials: '',
  });

  // Accept 1, "1", true, "true" as truthy; everything else falsy.
  const toBool = (v: any): boolean =>
    v === true || v === 1 || v === '1' || v === 'true';

  // 1 = active, 0 = inactive (backend contract).
  const toFlag = (v: any): 0 | 1 => (toBool(v) ? 1 : 0);

  /* ================= SNACKBAR HELPERS ================= */
  const showSnackbar = (
    message: string,
    severity: "success" | "error" | "info" | "warning" = "success"
  ) => {
    setSnackbarMsg(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (
    _event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  /* ================= FETCH API ================= */
  const fetchSupplierConfigs = async () => {
    try {
      setLoading(true);
      const response = await getSupplierConfigurationsAPI();

      const rawData = response?.data ?? response ?? [];

      const normalizedData: SupplierData[] = (Array.isArray(rawData) ? rawData : []).map(
        (item: any) => ({
          id: item.id,
          supplier_name: item.supplier_name ?? '',
          supplier_code: Number(item.supplier_code) || 0,
          supplier_source: item.supplier_source ?? '',
          office_id: item.office_id ?? null,
          environment: item.environment ?? '',
          timeout: Number(item.timeout) || 0,
          result_priority: Number(item.result_priority) || 0,
          enable_family_fare: toBool(item.enable_family_fare),
          enable_pnr_action: toBool(item.enable_pnr_action),
          is_active: toBool(item.is_active),
          created_date: item.created_date ?? null,
          modified_date: item.modified_date ?? '',
          supplier_charges: Number(item.supplier_charges) || 0,
          charges_type: item.charges_type ?? null,
          per_booking_charges: Number(item.per_booking_charges) || 0,
          per_booking_charges_type: item.per_booking_charges_type ?? null,
          discount: String(item.discount ?? '0'),
          discount_type: item.discount_type ?? null,
          discount_on: item.discount_on ?? null,
          charges_on: item.charges_on ?? null,
        })
      );

      setSuppliers(normalizedData);
    } catch (err) {
      console.error('Failed to fetch supplier configurations:', err);
      showSnackbar('Failed to fetch supplier configurations', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSupplierConfigs();
  }, []);

  /* ================= VIEW API ================= */
  const toggleViewRow = async (id: number) => {
    if (openRowId === id) {
      setOpenRowId(null);
      setOpenedByEdit(false);
      return;
    }

    setOpenRowId(id);
    setEditRowId(null);
    // Opened via View — Cancel from edit should NOT collapse it.
    setOpenedByEdit(false);

    if (viewDataMap[id]) return;

    const supplier = suppliers.find((s) => s.id === id);
    if (!supplier) return;

    try {
      setViewLoading(true);
      const payload = {
        supplier_name: supplier.supplier_name,
        supplier_source: supplier.supplier_source,
        environment: supplier.environment,
        office_id: supplier.office_id,
      };

      const response = await viewSupplierConfigurationAPI(payload);

      const viewResult = response?.data ?? response ?? {};

      // Status booleans (is_active, enable_family_fare, enable_pnr_action) are
      // owned by the list endpoint. View detail only contributes charges/discount
      // fields, so the Edit form always sees the list's source-of-truth status.
      const normalizedViewData: Partial<SupplierData> = {
        timeout: Number(viewResult.timeout) || supplier.timeout,
        result_priority: Number(viewResult.result_priority) || supplier.result_priority,
        enable_family_fare: supplier.enable_family_fare,
        enable_pnr_action: supplier.enable_pnr_action,
        is_active: supplier.is_active,
        supplier_charges: Number(viewResult.supplier_charges) || 0,
        charges_type: viewResult.charges_type ?? null,
        per_booking_charges: Number(viewResult.per_booking_charges) || 0,
        per_booking_charges_type: viewResult.per_booking_charges_type ?? null,
        discount: String(viewResult.discount ?? '0'),
        discount_type: viewResult.discount_type ?? null,
        discount_on: viewResult.discount_on ?? null,
        charges_on: viewResult.charges_on ?? null,
      };

      setViewDataMap((prev) => ({ ...prev, [id]: normalizedViewData }));

      setSuppliers((prev) =>
        prev.map((s) => (s.id === id ? { ...s, ...normalizedViewData } : s))
      );
    } catch (err) {
      console.error('Failed to fetch supplier view details:', err);
      showSnackbar('Failed to load supplier details', 'error');
    } finally {
      setViewLoading(false);
    }
  };

  const toggleEditRow = (id: number, supplier: SupplierData) => {
    if (editRowId === id) {
      setEditRowId(null);
      setEditedData({});
    } else {
      // If the row wasn't already open via View, mark this open as edit-initiated
      // so a subsequent Cancel collapses the panel instead of leaving it expanded.
      if (openRowId !== id) setOpenedByEdit(true);
      setEditRowId(id);
      setOpenRowId(id);
      // Coerce status booleans defensively so the Switch always reflects
      // the row's actual is_active state when entering edit mode.
      setEditedData({
        ...supplier,
        is_active: toBool(supplier.is_active),
        enable_family_fare: toBool(supplier.enable_family_fare),
        enable_pnr_action: toBool(supplier.enable_pnr_action),
      });
    }
  };

  /* ================= UPDATE API ================= */
  const handleSave = async (id: number) => {
    const originalSupplier = suppliers.find((s) => s.id === id);
    if (!originalSupplier) return;

    const hasChanges = Object.keys(editedData).some((key) => {
      const field = key as keyof SupplierData;
      return editedData[field] !== originalSupplier[field];
    });

    if (!hasChanges) {
      showSnackbar("Didn't update anything", "info");
      setEditRowId(null);
      setEditedData({});
      return;
    }

    try {
      setSaveLoading(true);

      const merged = { ...originalSupplier, ...editedData };

      const updatePayload = {
        id: merged.id,
        supplier_name: merged.supplier_name || '',
        supplier_code: merged.supplier_code || '',
        supplier_source: merged.supplier_source || '',
        environment: merged.environment || '',
        office_id: merged.office_id || null,
        timeout: merged.timeout || '',
        result_priority: merged.result_priority || '',
        enable_family_fare: toFlag(merged.enable_family_fare),
        enable_pnr_action: toFlag(merged.enable_pnr_action),
        is_active: toFlag(merged.is_active),
        supplier_charges: merged.supplier_charges || '',
        charges_type: merged.charges_type || null,
        per_booking_charges: merged.per_booking_charges || '',
        per_booking_charges_type: merged.per_booking_charges_type || null,
        discount: merged.discount || '',
        discount_type: merged.discount_type || null,
        discount_on: merged.discount_on || null,
        charges_on: merged.charges_on || null,
      };

      await updateSupplierConfigurationAPI(updatePayload);

      showSnackbar('Supplier configuration updated successfully', 'success');

      setViewDataMap((prev) => {
        const newMap = { ...prev };
        delete newMap[id];
        return newMap;
      });

      await fetchSupplierConfigs();

      setEditRowId(null);
      setEditedData({});
      // If the panel was opened just to edit, collapse it after a successful save.
      if (openedByEdit) {
        setOpenRowId(null);
        setOpenedByEdit(false);
      }
    } catch (err) {
      console.error('Failed to update supplier configuration:', err);
      showSnackbar('Failed to update supplier configuration', 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCancel = () => {
    setEditRowId(null);
    setEditedData({});
    // If the panel was opened by clicking Edit (not View), collapse it on Cancel
    // so the user returns to the table without lingering detail.
    if (openedByEdit) {
      setOpenRowId(null);
      setOpenedByEdit(false);
    }
  };

  const handleEditChange = (field: keyof SupplierData, value: any) => {
    setEditedData((prev) => ({ ...prev, [field]: value }));
  };

  const handleModalOpen = () => setOpenModal(true);
  const handleModalClose = () => {
    setOpenModal(false);
    setNewSupplierErrors({});
    setNewSupplier({
      supplier_name: '',
      supplier_code: 0,
      supplier_source: '',
      environment: '',
      office_id: null,
      timeout: 0,
      result_priority: 0,
      enable_family_fare: false,
      enable_pnr_action: false,
      is_active: false,
      supplier_charges: 0,
      charges_type: null,
      per_booking_charges: 0,
      per_booking_charges_type: null,
      discount: '',
      discount_type: null,
      discount_on: null,
      charges_on: null,
      encrypted_key: '',
      credentials: '',
    });
  };

  const handleNewSupplierChange = (field: keyof SupplierData, value: any) => {
    setNewSupplier((prev) => ({ ...prev, [field]: value }));
    setNewSupplierErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  /* ================= VALIDATION ================= */
  const validateNewSupplier = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!newSupplier.supplier_name?.trim()) errors.supplier_name = 'Please fill the fields';
    if (!newSupplier.supplier_code) errors.supplier_code = 'Please fill the fields';
    if (!newSupplier.supplier_source?.trim()) errors.supplier_source = 'Please fill the fields';
    if (!newSupplier.environment) errors.environment = 'Please fill the fields';
    if (!newSupplier.timeout) errors.timeout = 'Please fill the fields';
    if (!newSupplier.result_priority) errors.result_priority = 'Please fill the fields';
    return errors;
  };

  /* ================= SAVE NEW API ================= */
  const handleSaveNewSupplier = async () => {
    const errors = validateNewSupplier();
    if (Object.keys(errors).length > 0) {
      setNewSupplierErrors(errors);
      showSnackbar('Please fill all required fields', 'warning');
      return;
    }
    setNewSupplierErrors({});
    try {
      setSaveLoading(true);

      const payload = {
        supplier_name: newSupplier.supplier_name || '',
        supplier_code: newSupplier.supplier_code || '',
        supplier_source: newSupplier.supplier_source || '',
        environment: newSupplier.environment || '',
        office_id: newSupplier.office_id || null,
        timeout: newSupplier.timeout || '',
        result_priority: newSupplier.result_priority || '',
        enable_family_fare: toFlag(newSupplier.enable_family_fare),
        enable_pnr_action: toFlag(newSupplier.enable_pnr_action),
        is_active: toFlag(newSupplier.is_active),
        supplier_charges: newSupplier.supplier_charges || '',
        charges_type: newSupplier.charges_type || null,
        per_booking_charges: newSupplier.per_booking_charges || '',
        per_booking_charges_type: newSupplier.per_booking_charges_type || null,
        discount: newSupplier.discount || '',
        discount_type: newSupplier.discount_type || null,
        discount_on: newSupplier.discount_on || null,
        charges_on: newSupplier.charges_on || null,
      };

      await saveSupplierConfigurationAPI(payload);

      showSnackbar('New supplier configuration saved successfully', 'success');
      handleModalClose();
      await fetchSupplierConfigs();
    } catch (err) {
      console.error('Failed to save new supplier configuration:', err);
      showSnackbar('Failed to save new supplier configuration', 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  const capitalize = (value?: string | null) => {
    if (!value) return '-';
    return value
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getEnvironmentColor = (env: string) => {
    switch (env.toLowerCase()) {
      case 'production':
        return { textColor: '#059669', bgColor: '#d1fae5', borderColor: '#a7f3d0' };
      case 'staging':
        return { textColor: '#d97706', bgColor: '#fef3c7', borderColor: '#fde68a' };
      case 'development':
        return { textColor: '#2563eb', bgColor: '#dbeafe', borderColor: '#93c5fd' };
      default:
        return { textColor: '#6b7280', bgColor: '#f3f4f6', borderColor: '#d1d5db' };
    }
  };

  const filteredSuppliers = suppliers.filter((supplier) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      supplier.supplier_name.toLowerCase().includes(searchLower) ||
      supplier.supplier_source.toLowerCase().includes(searchLower) 
    );
  });

  const isEditing = (id: number) => editRowId === id;

  return (
    <>
      <Box className="headingBtnWrap" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Supplier Configuration
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleModalOpen}
          sx={{
            backgroundColor: '#272727',
            '&:hover': { backgroundColor: '#1a1a1a' },
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Add New
        </Button>
      </Box>

      {/* Add New Supplier Modal */}
      <Modal
        open={openModal}
        onClose={handleModalClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
        }}
      >
        <Fade in={openModal}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: '#f5f5f5',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            className="supplierConfigurationPopup"
          >
              <CancelIcon onClick={handleModalClose} className='closeModal' />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Add New Supplier
              </Typography>

              <Box className="feeModalForm">
                <Box className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  {/* Supplier Name */}
                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                    <label>Supplier Name</label>
                    <input
                      type="text"
                      className="inputField"
                      style={{ minWidth: '260px', height: '38px', ...(newSupplierErrors.supplier_name ? { border: '1px solid red' } : {}) }}
                      value={newSupplier.supplier_name || ''}
                      onChange={(e) => handleNewSupplierChange('supplier_name', e.target.value)}
                    />
                  </Box>

                  {/* Supplier Code */}
                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                    <label>Supplier Code</label>
                    <input
                      type="number"
                      className="inputField"
                      style={{ minWidth: '260px', height: '38px', ...(newSupplierErrors.supplier_code ? { border: '1px solid red' } : {}) }}
                      value={newSupplier.supplier_code || ''}
                      onChange={(e) => handleNewSupplierChange('supplier_code', e.target.value)}
                    />
                  </Box>

                  {/* Supplier Source */}
                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                    <label>Supplier Source</label>
                    <input
                      type="text"
                      className="inputField"
                      style={{ minWidth: '260px', height: '38px', ...(newSupplierErrors.supplier_source ? { border: '1px solid red' } : {}) }}
                      value={newSupplier.supplier_source || ''}
                      onChange={(e) => handleNewSupplierChange('supplier_source', e.target.value)}
                    />
                  </Box>

                  {/* Environment */}
                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                    <label>Environment</label>
                    <FormControl sx={{ minWidth: 260, ...(newSupplierErrors.environment ? { '& .commondropdown': { border: '1px solid red !important' } } : {}) }} className="commondropdownWrap">
                      <Select
                        className="commondropdown superdropdown"
                        value={newSupplier.environment || ''}
                        onChange={(e) => handleNewSupplierChange('environment', e.target.value)}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) {
                            return <span style={{ color: '#888' }}>Select</span>;
                          }
                          return capitalize(selected);
                        }}
                      >
                        <MenuItem value="">Select</MenuItem>
                        <MenuItem value="production">Production</MenuItem>
                        <MenuItem value="staging">Staging</MenuItem>
                        <MenuItem value="development">Development</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Office ID */}
                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                    <label>Office ID</label>
                    <input
                      type="text"
                      className="inputField"
                      style={{ minWidth: '260px', height: '38px' }}
                      value={newSupplier.office_id || ''}
                      onChange={(e) => handleNewSupplierChange('office_id', e.target.value)}
                    />
                  </Box>

                  {/* Timeout */}
                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                    <label>Timeout</label>
                    <input
                      type="number"
                      className="inputField"
                      style={{ minWidth: '260px', height: '38px', ...(newSupplierErrors.timeout ? { border: '1px solid red' } : {}) }}
                      value={newSupplier.timeout || ''}
                      onChange={(e) => handleNewSupplierChange('timeout', e.target.value)}
                    />
                  </Box>

                  {/* Encrypted Key */}
                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                    <label>Encrypted Key</label>
                    <input
                      type="text"
                      className="inputField"
                      style={{ minWidth: '260px', height: '38px' }}
                      value={newSupplier.encrypted_key || ''}
                      onChange={(e) => handleNewSupplierChange('encrypted_key', e.target.value)}
                    />
                    {newSupplierErrors.encrypted_key && (
                      <span className='errorNewSupplier'>
                        {newSupplierErrors.encrypted_key}
                      </span>
                    )}
                  </Box>
                  

                  {/* Credentials */}
                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                    <label>Credentials</label>
                    <input
                      type="text"
                      className="inputField"
                      style={{ minWidth: '260px', height: '38px' }}
                      value={newSupplier.credentials || ''}
                      onChange={(e) => handleNewSupplierChange('credentials', e.target.value)}
                    />
                    {newSupplierErrors.credentials && (
                      <span className='errorNewSupplier'>
                        {newSupplierErrors.credentials}
                      </span>
                    )}
                  </Box>
                  

                  {/* Result Priority */}
                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                    <label>Result Priority</label>
                    <input
                      type="number"
                      className="inputField"
                      style={{ minWidth: '260px', height: '38px', ...(newSupplierErrors.result_priority ? { border: '1px solid red' } : {}) }}
                      value={newSupplier.result_priority || ''}
                      onChange={(e) => handleNewSupplierChange('result_priority', e.target.value)}
                    />
                  </Box>

                  {/* Enable Family Fare */}
                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                    <label>Enable Family Fare</label>
                    <FormControl sx={{ minWidth: 260 }} className="commondropdownWrap">
                      <Select
                        className="commondropdown superdropdown"
                        value={newSupplier.enable_family_fare || ''}
                        onChange={(e) => handleNewSupplierChange('enable_family_fare', e.target.value)}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) {
                            return <span style={{ color: '#888' }}>Select Fare</span>;
                          }
                          return (selected);
                        }}
                      >
                        <MenuItem value="">Select Fare</MenuItem>
                        <MenuItem value="true">True</MenuItem>
                        <MenuItem value="false">False</MenuItem>
                      </Select>
                    </FormControl>
                    {newSupplierErrors.enable_family_fare && (
                      <span className='errorNewSupplier'>
                        {newSupplierErrors.enable_family_fare}
                      </span>
                    )}
                  </Box>

                  {/* Enable PNR Action */}
                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                    <label>Enable PNR Action</label>
                    <FormControl sx={{ minWidth: 260 }} className="commondropdownWrap">
                      <Select
                        className="commondropdown superdropdown"
                        value={newSupplier.enable_pnr_action || ''}
                        onChange={(e) => handleNewSupplierChange('enable_pnr_action', e.target.value)}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) {
                            return <span style={{ color: '#888' }}>Select Action</span>;
                          }
                          return (selected);
                        }}
                      >
                        <MenuItem value="">Select Action</MenuItem>
                        <MenuItem value="true">True</MenuItem>
                        <MenuItem value="false">False</MenuItem>
                      </Select>
                    </FormControl>
                    {newSupplierErrors.enable_pnr_action && (
                      <span className='errorNewSupplier'>
                        {newSupplierErrors.enable_pnr_action}
                      </span>
                    )}
                  </Box>

                  {/* Status */}
                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                    <label>Status</label>
                    <Box
                      onClick={() =>
                        handleNewSupplierChange('is_active', !newSupplier.is_active)
                      }
                      sx={{
                        minWidth: 260,
                        height: 38,
                        px: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        border: '1px solid',
                        borderColor: newSupplier.is_active ? '#a7f3d0' : '#e5e7eb',
                        borderRadius: '4px',
                        bgcolor: newSupplier.is_active ? '#f0fdf4' : '#f9fafb',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s, border-color 0.15s',
                        userSelect: 'none',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: newSupplier.is_active ? '#059669' : '#9ca3af',
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: newSupplier.is_active ? '#059669' : '#6b7280',
                          }}
                        >
                          {newSupplier.is_active ? 'Active' : 'Inactive'}
                        </Typography>
                      </Box>
                      <Switch
                        size="small"
                        checked={!!newSupplier.is_active}
                        color="success"
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleNewSupplierChange('is_active', e.target.checked)}
                      />
                    </Box>
                  </Box>

                  {/* Supplier Charges */}
                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                    <label>Supplier Charges</label>
                    <input
                      type="number"
                      className="inputField"
                      style={{ minWidth: '260px', height: '38px' }}
                      value={newSupplier.supplier_charges || ''}
                      onChange={(e) => handleNewSupplierChange('supplier_charges', e.target.value)}
                    />
                    {newSupplierErrors.supplier_charges && (
                      <span className='errorNewSupplier'>
                        {newSupplierErrors.supplier_charges}
                      </span>
                    )}
                  </Box>

                  {/* Charges Type */}
                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                    <label>Charges Type</label>
                    <FormControl sx={{ minWidth: 260 }} className="commondropdownWrap">
                      <Select
                        className="commondropdown superdropdown"
                        value={newSupplier.charges_type || ''}
                        onChange={(e) => handleNewSupplierChange('charges_type', e.target.value)}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) {
                            return <span style={{ color: '#888' }}>Select</span>;
                          }
                          return capitalize(selected);
                        }}
                      >
                        <MenuItem value="">Select</MenuItem>
                        <MenuItem value="value">Value</MenuItem>
                        <MenuItem value="percentage">Percentage</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Per Booking Charges */}
                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                    <label>Per Booking Charges</label>
                    <input
                      type="number"
                      className="inputField"
                      style={{ minWidth: '260px', height: '38px' }}
                      value={newSupplier.per_booking_charges || ''}
                      onChange={(e) => handleNewSupplierChange('per_booking_charges', e.target.value)}
                    />
                    {newSupplierErrors.per_booking_charges && (
                      <span className='errorNewSupplier'>
                        {newSupplierErrors.per_booking_charges}
                      </span>
                    )}
                  </Box>

                  {/* Per Booking Charges Type */}
                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                    <label>Per Booking Charges Type</label>
                    <FormControl sx={{ minWidth: 260 }} className="commondropdownWrap">
                      <Select
                        className="commondropdown superdropdown"
                        value={newSupplier.per_booking_charges_type || ''}
                        onChange={(e) => handleNewSupplierChange('per_booking_charges_type', e.target.value)}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) {
                            return <span style={{ color: '#888' }}>Select</span>;
                          }
                          return capitalize(selected);
                        }}
                      >
                        <MenuItem value="">Select</MenuItem>
                        <MenuItem value="value">Value</MenuItem>
                        <MenuItem value="percentage">Percentage</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Discount */}
                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                    <label>Discount</label>
                    <input
                      type="number"
                      className="inputField"
                      style={{ minWidth: '260px', height: '38px' }}
                      value={newSupplier.discount || ''}
                      onChange={(e) => handleNewSupplierChange('discount', e.target.value)}
                    />
                    {newSupplierErrors.discount && (
                      <span className='errorNewSupplier'>
                        {newSupplierErrors.discount}
                      </span>
                    )}
                  </Box>

                  {/* Discount Type */}
                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                    <label>Discount Type</label>
                    <FormControl sx={{ minWidth: 260 }} className="commondropdownWrap">
                      <Select
                        className="commondropdown superdropdown"
                        value={newSupplier.discount_type || ''}
                        onChange={(e) => handleNewSupplierChange('discount_type', e.target.value)}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) {
                            return <span style={{ color: '#888' }}>Select</span>;
                          }
                          return capitalize(selected);
                        }}
                      >
                        <MenuItem value="">Select</MenuItem>
                        <MenuItem value="value">Value</MenuItem>
                        <MenuItem value="percentage">Percentage</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Discount On */}
                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                    <label>Discount On</label>
                    <input
                      type="text"
                      className="inputField"
                      style={{ minWidth: '260px', height: '38px' }}
                      value={newSupplier.discount_on || ''}
                      onChange={(e) => handleNewSupplierChange('discount_on', e.target.value)}
                    />
                  </Box>

                  {/* Charges On */}
                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                    <label>Charges On</label>
                    <input
                      type="text"
                      className="inputField"
                      style={{ minWidth: '260px', height: '38px' }}
                      value={newSupplier.charges_on || ''}
                      onChange={(e) => handleNewSupplierChange('charges_on', e.target.value)}
                    />
                  </Box>
                </Box>

                {/* Save Button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveNewSupplier}
                    disabled={saveLoading}
                    sx={{
                      backgroundColor: '#272727',
                      '&:hover': { backgroundColor: '#272727' },
                      textTransform: 'none',
                    }}
                  >
                    {saveLoading ? <CircularProgress size={24} color="inherit" /> : 'SAVE'}
                  </Button>
                </Box>
              </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Table */}
      <Box
        sx={{
          border: '1px solid #e5e7eb',
          borderRadius: 2,
          overflow: 'hidden',
        }}
        className="supplierConfiguration"
      >
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: '75vh',
            overflowY: 'auto',
            backgroundColor: '#ffffff',
          }}
        >
          <Table stickyHeader>
            <TableHead
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                backgroundColor: "#f1f3f4",
              }}
            >
              <TableRow
                sx={{
              boxShadow: "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px",
            }}
              >
                {['SUPPLIER NAME', 'SUPPLIER SOURCE', 'OFFICE ID', 'ENVIRONMENT', 'ACTIONS'].map(
                  (label) => (
                    <TableCell
                      key={label}
                      align="center"
                      sx={{
                        backgroundColor: '#f1f3f4',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                      }}
                    >
                      {label}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredSuppliers.map((supplier) => {
                const envColor = getEnvironmentColor(supplier.environment);
                const isOpen = openRowId === supplier.id;
                const editing = isEditing(supplier.id);
                const currentData = editing ? { ...supplier, ...editedData } : supplier;

                return (
                  <React.Fragment key={supplier.id}>
                    {/* Main Row */}
                    <TableRow
                      hover
                      sx={{
                        '&:hover': { backgroundColor: '#f9fafb' },
                      }}
                    >
                      <TableCell align="center">
                        {capitalize(supplier.supplier_name)}
                      </TableCell>

                      <TableCell align="center">
                        {capitalize(supplier.supplier_source)}
                      </TableCell>

                      <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {supplier.office_id}
                      </TableCell>

                      <TableCell align="center">
                        <Box
                          sx={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '16px',
                            backgroundColor: envColor.bgColor,
                            color: envColor.textColor,
                            border: `1px solid ${envColor.borderColor}`,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            textTransform: 'capitalize',
                          }}
                        >
                          {supplier.environment}
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {editing ? (
                            <>
                              <Tooltip title="Save">
                                <IconButton
                                  size="small"
                                  onClick={() => handleSave(supplier.id)}
                                  disabled={saveLoading}
                                  sx={{
                                    color: '#059669',
                                    '&:hover': { backgroundColor: '#d1fae5' },
                                  }}
                                >
                                  {saveLoading ? <CircularProgress size={20} /> : <Save />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton
                                  size="small"
                                  onClick={handleCancel}
                                  sx={{
                                    color: '#dc2626',
                                    '&:hover': { backgroundColor: '#fee2e2' },
                                  }}
                                >
                                  <Close />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => toggleViewRow(supplier.id)}
                              >
                                <Visibility />
                              </IconButton>
                              <IconButton
                                color="secondary"
                                size="small"
                                onClick={() => toggleEditRow(supplier.id, supplier)}
                              >
                                <Edit />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Collapsible Details Row */}
                    <TableRow>
                      <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 4, bgcolor: '#f5f5f5' }}>
                            {viewLoading && !editing ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                                <CircularProgress />
                              </Box>
                            ) : (
                            <Box className="feeModalForm" sx={{ bgcolor: '#fff', p: 3, borderRadius: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  Supplier Configuration Details
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  {/* Status pill — sits next to the Update button */}
                                  <Box
                                    onClick={() =>
                                      editing && handleEditChange('is_active', !currentData.is_active)
                                    }
                                    sx={{
                                      height: 38,
                                      px: 1.5,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1.25,
                                      border: '1px solid',
                                      borderColor: currentData.is_active ? '#a7f3d0' : '#e5e7eb',
                                      borderRadius: '999px',
                                      bgcolor: currentData.is_active ? '#f0fdf4' : '#f9fafb',
                                      cursor: editing ? 'pointer' : 'default',
                                      transition: 'background-color 0.15s, border-color 0.15s',
                                      userSelect: 'none',
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: currentData.is_active ? '#059669' : '#9ca3af',
                                        boxShadow: currentData.is_active
                                          ? '0 0 0 3px rgba(5, 150, 105, 0.15)'
                                          : 'none',
                                      }}
                                    />
                                    <Typography
                                      sx={{
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        color: currentData.is_active ? '#059669' : '#6b7280',
                                        letterSpacing: '0.2px',
                                      }}
                                    >
                                      {currentData.is_active ? 'Active' : 'Inactive'}
                                    </Typography>
                                    <Switch
                                      size="small"
                                      checked={!!currentData.is_active}
                                      color="success"
                                      disabled={!editing}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) => handleEditChange('is_active', e.target.checked)}
                                      sx={{ ml: 0.25 }}
                                    />
                                  </Box>
                                  {editing && (
                                    <Button
                                      variant="contained"
                                      onClick={() => handleSave(supplier.id)}
                                      disabled={saveLoading}
                                      sx={{
                                        backgroundColor: '#272727',
                                        '&:hover': { backgroundColor: '#1a1a1a' },
                                        textTransform: 'none',
                                        fontWeight: 600,
                                      }}
                                    >
                                      {saveLoading ? <CircularProgress size={24} color="inherit" /> : 'Update'}
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                              <Box className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                {/* Timeout */}
                                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                                  <label>Timeout</label>
                                  {editing ? (
                                    <input
                                      type="number"
                                      className="inputField"
                                      style={{ minWidth: '265px', height: '38px' }}
                                      value={currentData.timeout}
                                      onChange={(e) => handleEditChange('timeout', parseInt(e.target.value))}
                                    />
                                  ) : (
                                    <Typography sx={{ fontSize: '14px', color: '#333' }}>{currentData.timeout}s</Typography>
                                  )}
                                </Box>

                                {/* Result Priority */}
                                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                                  <label>Result Priority</label>
                                  {editing ? (
                                    <input
                                      type="number"
                                      className="inputField"
                                      style={{ minWidth: '265px', height: '38px' }}
                                      value={currentData.result_priority}
                                      onChange={(e) => handleEditChange('result_priority', parseInt(e.target.value))}
                                    />
                                  ) : (
                                    <Typography sx={{ fontSize: '14px', color: '#333' }}>{currentData.result_priority}</Typography>
                                  )}
                                </Box>

                                {/* Enable Family Fare */}
                                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                                  <label>Enable Family Fare</label>
                                  {editing ? (
                                    <FormControl sx={{ minWidth: 265 }} className="commondropdownWrap">
                                      <Select
                                        className="commondropdown superdropdown"
                                        value={currentData.enable_family_fare ? 'true' : 'false'}
                                        onChange={(e) => handleEditChange('enable_family_fare', e.target.value === 'true')}
                                        displayEmpty
                                        renderValue={(selected) => {
                                          return selected === 'true' ? 'True' : 'False';
                                        }}
                                      >
                                        <MenuItem value="true">True</MenuItem>
                                        <MenuItem value="false">False</MenuItem>
                                      </Select>
                                    </FormControl>
                                  ) : (
                                    <Typography sx={{ fontSize: '14px', color: '#333' }}>{currentData.enable_family_fare ? 'True' : 'False'}</Typography>
                                  )}
                                </Box>

                                {/* Enable PNR Action */}
                                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                                  <label>Enable PNR Action</label>
                                  {editing ? (
                                    <FormControl sx={{ minWidth: 265 }} className="commondropdownWrap">
                                      <Select
                                        className="commondropdown superdropdown"
                                        value={currentData.enable_pnr_action ? 'true' : 'false'}
                                        onChange={(e) => handleEditChange('enable_pnr_action', e.target.value === 'true')}
                                        displayEmpty
                                        renderValue={(selected) => {
                                          return selected === 'true' ? 'True' : 'False';
                                        }}
                                      >
                                        <MenuItem value="true">True</MenuItem>
                                        <MenuItem value="false">False</MenuItem>
                                      </Select>
                                    </FormControl>
                                  ) : (
                                    <Typography sx={{ fontSize: '14px', color: '#333' }}>{currentData.enable_pnr_action ? 'True' : 'False'}</Typography>
                                  )}
                                </Box>

                                {/* Supplier Charges */}
                                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                                  <label>Supplier Charges</label>
                                  {editing ? (
                                    <input
                                      type="number"
                                      className="inputField"
                                      style={{ minWidth: '265px', height: '38px' }}
                                      value={currentData.supplier_charges}
                                      onChange={(e) => handleEditChange('supplier_charges', parseFloat(e.target.value))}
                                    />
                                  ) : (
                                    <Typography sx={{ fontSize: '14px', color: '#333' }}>${currentData.supplier_charges.toFixed(2)}</Typography>
                                  )}
                                </Box>

                                {/* Charges Type */}
                                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                                  <label>Charges Type</label>
                                  {editing ? (
                                    <FormControl sx={{ minWidth: 265 }} className="commondropdownWrap">
                                      <Select
                                        className="commondropdown superdropdown"
                                        value={currentData.charges_type || ''}
                                        onChange={(e) => handleEditChange('charges_type', e.target.value)}
                                        displayEmpty
                                        renderValue={(selected) => {
                                          if (!selected) {
                                            return <span style={{ color: '#888' }}>Select Type</span>;
                                          }
                                          return capitalize(selected);
                                        }}
                                      >
                                        <MenuItem value="value">Value</MenuItem>
                                        <MenuItem value="percentage">Percentage</MenuItem>
                                      </Select>
                                    </FormControl>
                                  ) : (
                                    <Typography sx={{ fontSize: '14px', color: '#333' }}>{capitalize(currentData.charges_type)}</Typography>
                                  )}
                                </Box>

                                {/* Per Booking Charges */}
                                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                                  <label>Per Booking Charges</label>
                                  {editing ? (
                                    <input
                                      type="number"
                                      className="inputField"
                                      style={{ minWidth: '265px', height: '38px' }}
                                      value={currentData.per_booking_charges}
                                      onChange={(e) => handleEditChange('per_booking_charges', parseFloat(e.target.value))}
                                    />
                                  ) : (
                                    <Typography sx={{ fontSize: '14px', color: '#333' }}>${currentData.per_booking_charges.toFixed(2)}</Typography>
                                  )}
                                </Box>

                                {/* Per Booking Charges Type */}
                                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                                  <label>Per Booking Charges Type</label>
                                  {editing ? (
                                    <FormControl sx={{ minWidth: 265 }} className="commondropdownWrap">
                                      <Select
                                        className="commondropdown superdropdown"
                                        value={currentData.per_booking_charges_type || ''}
                                        onChange={(e) => handleEditChange('per_booking_charges_type', e.target.value)}
                                        displayEmpty
                                        renderValue={(selected) => {
                                          if (!selected) {
                                            return <span style={{ color: '#888' }}>Select Type</span>;
                                          }
                                          return capitalize(selected);
                                        }}
                                      >
                                        <MenuItem value="value">Value</MenuItem>
                                        <MenuItem value="percentage">Percentage</MenuItem>
                                      </Select>
                                    </FormControl>
                                  ) : (
                                    <Typography sx={{ fontSize: '14px', color: '#333' }}>{capitalize(currentData.per_booking_charges_type)}</Typography>
                                  )}
                                </Box>

                                {/* Discount */}
                                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                                  <label>Discount</label>
                                  {editing ? (
                                    <input
                                      type="number"
                                      className="inputField"
                                      style={{ minWidth: '265px', height: '38px' }}
                                      value={currentData.discount}
                                      onChange={(e) => handleEditChange('discount', e.target.value)}
                                    />
                                  ) : (
                                    <Typography sx={{ fontSize: '14px', color: '#333' }}>${parseFloat(currentData.discount).toFixed(2)}</Typography>
                                  )}
                                </Box>

                                {/* Discount Type */}
                                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                                  <label>Discount Type</label>
                                  {editing ? (
                                    <FormControl sx={{ minWidth: 265 }} className="commondropdownWrap">
                                      <Select
                                        className="commondropdown superdropdown"
                                        value={currentData.discount_type || ''}
                                        onChange={(e) => handleEditChange('discount_type', e.target.value)}
                                        displayEmpty
                                        renderValue={(selected) => {
                                          if (!selected) {
                                            return <span style={{ color: '#888' }}>Select Type</span>;
                                          }
                                          return capitalize(selected);
                                        }}
                                      >
                                        <MenuItem value="value">Value</MenuItem>
                                        <MenuItem value="percentage">Percentage</MenuItem>
                                      </Select>
                                    </FormControl>
                                  ) : (
                                    <Typography sx={{ fontSize: '14px', color: '#333' }}>{capitalize(currentData.discount_type)}</Typography>
                                  )}
                                </Box>

                                {/* Discount On */}
                                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                                  <label>Discount On</label>
                                  {editing ? (
                                    <input
                                      type="text"
                                      className="inputField"
                                      style={{ minWidth: '265px', height: '38px' }}
                                      value={currentData.discount_on || ''}
                                      onChange={(e) => handleEditChange('discount_on', e.target.value)}
                                    />
                                  ) : (
                                    <Typography sx={{ fontSize: '14px', color: '#333' }}>{capitalize(currentData.discount_on)}</Typography>
                                  )}
                                </Box>

                                {/* Charges On */}
                                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                                  <label>Charges On</label>
                                  {editing ? (
                                    <input
                                      type="text"
                                      className="inputField"
                                      style={{ minWidth: '265px', height: '38px' }}
                                      value={currentData.charges_on || ''}
                                      onChange={(e) => handleEditChange('charges_on', e.target.value)}
                                    />
                                  ) : (
                                    <Typography sx={{ fontSize: '14px', color: '#333' }}>{capitalize(currentData.charges_on)}</Typography>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}

              {loading && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}

              {!loading && filteredSuppliers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 8, fontWeight: 600, color: 'text.secondary' }}>
                    No suppliers found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {/* Snackbar notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          top: "50% !important",
          left: "50% !important",
          transform: "translate(-50%, -50%) !important",
          '& .MuiSnackbarContent-root': {
            textAlign: 'center',
            minWidth: '200px',
          },
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '1rem',
          }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SupplierConfiguration;
